"use client";

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ArrowRightLeft, Check, ChevronDown, Plus, Split, Trash2 } from "lucide-react";
import {
  AeraButton,
  CardScrollRail,
  ModalCloseButton,
  ModalShell,
} from "@aera/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/components/ui/DropdownMenu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/components/ui/Tooltip";
import type { ReceivableListItem } from "@/lib/pendingOrders";
import {
  getReceivableExpiresAtMs,
  isReceivableAnnouncementItem,
} from "@/lib/pendingOrders";
import { formatAllocateUndoLabel } from "@/lib/moneyUndoToast";
import {
  pickReceivableRevision,
  postUnmatchPayment,
} from "@/lib/receivableCorrections";
import {
  createOperationKey,
  postAllocateMoney,
  postMoveAllocation,
  postResplitAllocation,
} from "@/lib/moneyOperations";
import { isReceivableUsableMoneyCandidate } from "@/lib/receivableDisplayStatus";

/** Horizontal rail on all breakpoints (credits page is md+ only by default). */
const ALLOCATION_RAIL_CLASSNAME =
  "flex flex-nowrap gap-3 overflow-x-auto overscroll-x-contain py-0 scrollbar-hide";

/** Same host as RecordDrawer / ReceivableRecordDrawer cancel sheets. */
const RECORD_DRAWER_PANEL_SELECTOR =
  "[data-aera-modal-panel].record-drawer-modal-panel";

function useMinWidthMd() {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setMatches(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return matches;
}

function pickField(item: ReceivableListItem, keys: string[]): string {
  for (const key of keys) {
    const value = item.fields.find((f) => f.key === key)?.value?.trim();
    if (value && value !== "—") return value;
  }
  return "";
}

function pickTitle(item: ReceivableListItem): string {
  return (
    pickField(item, ["customer_ref", "customer_name", "slip_sender"]) ||
    item.title?.trim() ||
    "—"
  );
}

function pickAmountLabel(item: ReceivableListItem): string {
  return (
    item.amountLabel?.trim() ||
    pickField(item, ["amount", "amount_remaining", "slip_amount"]) ||
    "—"
  );
}

function parseAmountNumber(label: string): number {
  const normalized = label.replace(/[^\d.,-]/g, "").replace(/,/g, "");
  if (!normalized) return 0;
  const value = Number.parseFloat(normalized);
  return Number.isFinite(value) ? value : 0;
}

/** Digits + at most one `.` — blocks letters and other symbols. */
function sanitizeAmountInput(raw: string): string {
  const digitsAndDots = raw.replace(/[^\d.]/g, "");
  const firstDot = digitsAndDots.indexOf(".");
  if (firstDot === -1) return digitsAndDots;
  return (
    digitsAndDots.slice(0, firstDot + 1) +
    digitsAndDots.slice(firstDot + 1).replace(/\./g, "")
  );
}

function formatBaht(value: number): string {
  return value.toLocaleString("th-TH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function amountInputValue(value: number): string {
  if (!Number.isFinite(value) || value === 0) return "0";
  return String(Math.round(value * 100) / 100);
}

/** Summary for sticky undo bar / toast — same shape as history description. */
export type ResplitDoneInfo = {
  operationId: string | null;
  /** e.g. "จัดสรรใหม่ 509 บาท → เจปัง A 100 · เจปัง B 299 · อิจิโกะ 110" */
  label: string;
  cleared: boolean;
};

function buildResplitUndoLabel(
  parts: Array<{ label: string; amount: number }>,
  kind: "resplit" | "allocate" = "resplit",
): string {
  return formatAllocateUndoLabel(
    parts,
    kind === "allocate" ? "จับคู่ยอดค้าง" : "จัดสรรใหม่",
    "ยกเลิกจัดสรรทั้งหมด",
  );
}

type AddListSort =
  | "expiry"
  | "created_latest"
  | "name"
  | "amount_asc"
  | "amount_desc";

const ADD_LIST_SORT_OPTIONS: { id: AddListSort; label: string }[] = [
  { id: "expiry", label: "ใกล้หมดอายุ" },
  { id: "created_latest", label: "วันที่เพิ่มล่าสุด" },
  { id: "name", label: "ชื่อ" },
  { id: "amount_asc", label: "ยอดต่ำสุด" },
  { id: "amount_desc", label: "ยอดสูงสุด" },
];

function pickCreatedAtMs(item: ReceivableListItem): number | null {
  const raw =
    item.createdAt?.trim() ||
    item.fields.find((f) => f.key === "created_at")?.value?.trim() ||
    "";
  if (!raw || raw === "—") return null;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : null;
}

function pickSortAmount(item: ReceivableListItem): number {
  const remaining = item.fields.find((f) => f.key === "amount_remaining")?.value;
  if (remaining) {
    const parsed = parseAmountNumber(remaining);
    if (parsed > 0 || remaining.trim() === "0") return parsed;
  }
  return parseAmountNumber(pickAmountLabel(item));
}

function compareNullableMs(
  leftMs: number | null,
  rightMs: number | null,
  dir: "asc" | "desc",
): number {
  if (leftMs === null && rightMs === null) return 0;
  if (leftMs === null) return 1;
  if (rightMs === null) return -1;
  return dir === "asc" ? leftMs - rightMs : rightMs - leftMs;
}

function sortAddCandidates(
  items: ReceivableListItem[],
  sortBy: AddListSort,
): ReceivableListItem[] {
  return [...items].sort((left, right) => {
    if (sortBy === "expiry") {
      return compareNullableMs(
        getReceivableExpiresAtMs(left),
        getReceivableExpiresAtMs(right),
        "asc",
      );
    }
    if (sortBy === "created_latest") {
      return compareNullableMs(
        pickCreatedAtMs(left),
        pickCreatedAtMs(right),
        "desc",
      );
    }
    if (sortBy === "name") {
      return pickTitle(left).localeCompare(pickTitle(right), "th");
    }
    if (sortBy === "amount_asc") {
      return pickSortAmount(left) - pickSortAmount(right);
    }
    return pickSortAmount(right) - pickSortAmount(left);
  });
}

export function MoveAllocationModal({
  open,
  onOpenChange,
  onExited,
  sourceItem,
  allocationId,
  amount,
  paymentId,
  destinationCandidates,
  ownerUserId,
  workspaceId,
  onMoved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExited?: () => void;
  sourceItem: ReceivableListItem;
  allocationId: string;
  amount: string;
  paymentId: string;
  destinationCandidates: ReceivableListItem[];
  ownerUserId: string | null;
  workspaceId: string | null;
  onMoved?: (operationId: string | null) => void | Promise<void>;
}) {
  const sourceReceivableId =
    sourceItem.receivableId?.trim() ||
    pickField(sourceItem, ["receivable_id", "receivableId"]);

  const [destKey, setDestKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listSort, setListSort] = useState<AddListSort>("expiry");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const isDesktop = useMinWidthMd();
  const [scopedHost, setScopedHost] = useState<Element | null>(null);

  const destinations = useMemo(() => {
    const filtered = destinationCandidates.filter((item) => {
      if (!isReceivableUsableMoneyCandidate(item)) return false;
      if (!isReceivableAnnouncementItem(item)) return false;
      const rid =
        item.receivableId?.trim() ||
        pickField(item, ["receivable_id", "receivableId"]);
      return Boolean(rid) && rid !== sourceReceivableId;
    });
    return sortAddCandidates(filtered, listSort);
  }, [destinationCandidates, listSort, sourceReceivableId]);

  useLayoutEffect(() => {
    if (!isDesktop) {
      setScopedHost(null);
      return;
    }
    if (!open) return;
    setScopedHost(document.querySelector(RECORD_DRAWER_PANEL_SELECTOR));
  }, [isDesktop, open]);

  // Snapshot when the modal opens — parent often passes a fresh candidates
  // array each render; resetting on every change wiped the user's selection.
  useEffect(() => {
    if (!open) return;
    setDestKey(destinations[0]?.mergeKey ?? null);
    setError(null);
    setBusy(false);
    setListSort("expiry");
    setSortMenuOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open only
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setDestKey((prev) => {
      if (prev && destinations.some((d) => d.mergeKey === prev)) return prev;
      return destinations[0]?.mergeKey ?? null;
    });
  }, [open, destinations]);

  const isScoped = isDesktop && scopedHost != null;

  const selected = destinations.find((d) => d.mergeKey === destKey);
  /** ยอดที่จับคู่จากต้นทาง — ย้ายทั้งก้อน (ต้นทางไม่เหลือ) */
  const sourceAmount = parseAmountNumber(amount);
  /** ยอดค้างปลายทาง — รับได้เท่านี้ ส่วนเกินกลับรอจัดสรรของสลิป */
  const destRemaining = selected
    ? parseAmountNumber(pickAmountLabel(selected))
    : 0;
  const applyAmount =
    destRemaining > 0
      ? Math.min(sourceAmount, destRemaining)
      : 0;
  const paymentRemainder = Math.max(0, sourceAmount - applyAmount);
  /** ยอดค้างปลายทางสูงกว่าเงินต้นทาง → หลังย้ายยอดค้างยังเหลือ */
  const destKeepsWaiting =
    destRemaining > 0 && destRemaining > sourceAmount + 0.001;
  /** ยอดค้างน้อยกว่า/เท่าเงินต้นทาง → ยอดค้างนี้หมด */
  const destClearsFully =
    destRemaining > 0 && destRemaining <= sourceAmount + 0.001;
  const validationError = !selected
    ? null
    : sourceAmount <= 0
      ? "ไม่พบยอดที่จับคู่ให้ย้าย"
      : applyAmount <= 0
        ? "ไม่มียอดค้างให้ย้ายไป"
        : null;
  const canSubmit =
    Boolean(selected) && applyAmount > 0 && !busy;

  const handleSubmit = async () => {
    if (!ownerUserId?.trim() || !workspaceId?.trim() || !selected || !allocationId) {
      setError("ข้อมูลไม่ครบสำหรับย้ายยอด");
      return;
    }
    if (validationError) {
      setError(validationError);
      return;
    }
    const destId =
      selected.receivableId?.trim() ||
      pickField(selected, ["receivable_id", "receivableId"]);
    if (!destId) {
      setError("ไม่พบ receivable_id ของปลายทาง");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await postMoveAllocation({
        ownerUserId,
        workspaceId,
        allocationId,
        destinationReceivableId: destId,
        // Backend ย้ายทั้งก้อนจากต้นทาง — ไม่ส่ง amount แบบตัดบางส่วน
        expectedSourceRevision: pickReceivableRevision(sourceItem),
        expectedDestinationRevision: pickReceivableRevision(selected),
      });
      const opId =
        typeof result.after?.operation_id === "string"
          ? result.after.operation_id
          : typeof result.entity_id === "string"
            ? result.entity_id
            : null;
      await onMoved?.(opId);
      onOpenChange(false);
    } catch (err) {
      const raw = err instanceof Error ? err.message : "ย้ายยอดไม่สำเร็จ";
      const friendly =
        /destination has no remaining|exceeds destination remaining/i.test(raw)
          ? `ยอดค้างปลายทางไม่พอ${destRemaining > 0 ? ` (ค้าง ${formatBaht(destRemaining)} บาท)` : ""}`
          : /revision conflict/i.test(raw)
            ? "ข้อมูลยอดค้างเปลี่ยนระหว่างทาง — ปิดแล้วเปิดใหม่อีกครั้ง"
            : /no active allocation/i.test(raw)
              ? "ไม่พบการจับคู่ที่ยังใช้งานได้ — รีเฟรชแล้วลองใหม่"
              : raw;
      setError(friendly);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (busy) return;
    onOpenChange(false);
  };

  const outcomeHint =
    selected && applyAmount > 0
      ? paymentRemainder > 0.001
        ? `ต้นทางไม่เหลือ · ส่วนเกิน ฿${formatBaht(paymentRemainder)} กลับรอจัดสรร`
        : destKeepsWaiting
          ? `ยอดค้างปลายทางยังเหลือ ฿${formatBaht(destRemaining - applyAmount)}`
          : destClearsFully
            ? "ยอดค้างปลายทางนี้จะหมด"
            : null
      : null;

  const summaryLine =
    selected && applyAmount > 0 ? (
      <>
        {"ย้ายยอด "}
        <span className="font-semibold text-aera-charcoal">
          {pickTitle(sourceItem)}
        </span>{" "}
        <span className="tabular-nums">฿{formatBaht(sourceAmount)}</span>
        {" เป็น "}
        <span className="font-semibold text-aera-charcoal">
          {pickTitle(selected)}
        </span>
        {" จำนวน "}
        <span className="font-semibold tabular-nums text-aera-charcoal">
          ฿{formatBaht(applyAmount)}
        </span>
        {outcomeHint ? <> ({outcomeHint})</> : null}
      </>
    ) : (
      <>
        {"ย้ายยอด "}
        <span className="font-semibold text-aera-charcoal">
          {pickTitle(sourceItem)}
        </span>{" "}
        <span className="tabular-nums">฿{formatBaht(sourceAmount)}</span>
        {" — เลือกยอดค้างด้านล่าง"}
      </>
    );

  return (
    <ModalShell
      isOpen={open}
      onClose={close}
      onExited={() => {
        setScopedHost(null);
        onExited?.();
      }}
      align="bottom"
      scopedHost={isScoped ? scopedHost : null}
      preventBackdropClose={busy}
      zIndexClass="z-[10050]"
      panelClassName={[
        isScoped
          ? "flex flex-col overflow-hidden rounded-t-3xl rounded-b-[inherit] border-t border-aera-lightgray bg-aera-white shadow-xl"
          : "flex flex-col overflow-hidden rounded-t-3xl rounded-b-none border-t border-aera-lightgray bg-aera-white shadow-xl",
        isScoped ? "max-h-[85%]" : "max-h-[80dvh]",
      ].join(" ")}
    >
      <div className="flex shrink-0 justify-center pb-1 pt-2">
        <div className="h-1 w-10 rounded-full bg-aera-lightgray" aria-hidden />
      </div>

      <header className="flex shrink-0 items-start justify-between gap-3 px-4 pb-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-aera-charcoal">
            <span className="flex items-center gap-2 text-aera-amberdark">
              <ArrowRightLeft
                className="h-5 w-5 shrink-0"
                strokeWidth={2}
                aria-hidden
              />
              ยืนยันย้ายยอดที่จับคู่
            </span>
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-aera-gray">
            {summaryLine}
          </p>
        </div>
        <ModalCloseButton
          onClose={close}
          disabled={busy}
          className="-mr-1 shrink-0"
        />
      </header>

      {destinations.length > 0 ? (
        <div className="flex shrink-0 items-center justify-end gap-2 px-4 pb-2">
          <span className="text-xs font-medium text-aera-gray">เรียงตาม</span>
          <DropdownMenu open={sortMenuOpen} onOpenChange={setSortMenuOpen}>
            <DropdownMenuTrigger asChild>
              <AeraButton
                type="button"
                variant="outline"
                size="xs"
                fontWeight="semibold"
                className="min-w-0 max-w-[10.5rem]"
                disabled={busy}
                aria-label={`เรียงตาม: ${
                  ADD_LIST_SORT_OPTIONS.find((o) => o.id === listSort)?.label ??
                  ""
                }`}
                aria-expanded={sortMenuOpen}
                iconRight={
                  <ChevronDown
                    size={14}
                    className={`shrink-0 text-aera-gray transition-transform duration-200 ${
                      sortMenuOpen ? "rotate-180" : "rotate-0"
                    }`}
                  />
                }
              >
                <span className="truncate">
                  {ADD_LIST_SORT_OPTIONS.find((o) => o.id === listSort)
                    ?.label ?? "ใกล้หมดอายุ"}
                </span>
              </AeraButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={6}
              className="z-[10100] min-w-[11.5rem] rounded-xl border border-aera-lightgray/80 p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)]"
            >
              {ADD_LIST_SORT_OPTIONS.map((option) => {
                const active = listSort === option.id;
                return (
                  <DropdownMenuItem
                    key={option.id}
                    onClick={() => {
                      setListSort(option.id);
                      setSortMenuOpen(false);
                    }}
                    className="cursor-pointer justify-between gap-3 rounded-lg px-2.5 py-2 text-xs font-semibold text-aera-charcoal"
                  >
                    <span>{option.label}</span>
                    {active ? (
                      <Check
                        className="h-3.5 w-3.5 shrink-0 text-aera-amber"
                        strokeWidth={3}
                        aria-hidden
                      />
                    ) : (
                      <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    )}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-aera-charcoal">
        <div className="space-y-2">
          {destinations.length === 0 ? (
            <p className="rounded-xl border border-dashed border-aera-lightgray px-4 py-6 text-center text-sm text-aera-gray">
              ไม่มียอดค้างอื่นให้ย้ายไป
            </p>
          ) : (
            <ul className="space-y-2">
              {destinations.map((item) => {
                const selectedDest = destKey === item.mergeKey;
                const remaining = parseAmountNumber(pickAmountLabel(item));
                const willWait =
                  remaining > 0 && remaining > sourceAmount + 0.001;
                const willClear =
                  remaining > 0 && remaining <= sourceAmount + 0.001;
                return (
                  <li key={item.mergeKey}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setDestKey(item.mergeKey);
                        setError(null);
                      }}
                      className={`flex w-full cursor-pointer items-center justify-between gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors ${
                        selectedDest
                          ? "border-aera-amber bg-aera-amber/10"
                          : "border-aera-lightgray bg-white hover:border-aera-amber/40"
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-aera-charcoal">
                          {pickTitle(item)}
                        </span>
                        <span className="mt-0.5 block text-xs tabular-nums text-aera-gray">
                          ค้าง {pickAmountLabel(item)}
                          {selectedDest && willWait
                            ? " · รับทั้งก้อน · ยอดค้างยังเหลือ"
                            : selectedDest && willClear
                              ? " · รับทั้งก้อน · หมด"
                              : ""}
                        </span>
                      </span>
                      <span
                        className={`h-4 w-4 shrink-0 rounded-full border-2 ${
                          selectedDest
                            ? "border-aera-amber bg-aera-amber"
                            : "border-aera-lightgray bg-white"
                        }`}
                        aria-hidden
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {error || validationError ? (
          <p className="mt-3 rounded-lg bg-aera-redlight px-3 py-2 text-sm font-medium text-aera-red">
            {error || validationError}
          </p>
        ) : null}
      </div>

      <div className="shrink-0 space-y-3 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <AeraButton
          type="button"
          variant="primary"
          fontWeight="medium"
          fullWidth
          className="cursor-pointer rounded-full!"
          disabled={!canSubmit}
          loading={busy}
          onClick={() => void handleSubmit()}
        >
          {busy ? "กำลังย้าย…" : "ยืนยันย้าย"}
        </AeraButton>
        <AeraButton
          type="button"
          variant="outline"
          fontWeight="medium"
          fullWidth
          className="cursor-pointer rounded-full!"
          disabled={busy}
          onClick={close}
        >
          ปิด
        </AeraButton>
      </div>
    </ModalShell>
  );
}

export function ResplitAllocationSkeleton() {
  return (
    <div
      className="space-y-4 rounded-2xl border border-(--aera-dt-border-soft) bg-(--aera-dt-surface-elevated) p-4"
      aria-busy="true"
      aria-label="กำลังโหลดยอดจัดสรร"
    >
      <div className="space-y-1.5">
        <div className="h-3.5 w-24 animate-pulse rounded bg-aera-lightgray/80" />
        <div className="h-2.5 w-full max-w-sm animate-pulse rounded bg-aera-lightgray/70" />
      </div>
      <div className="space-y-2">
        <div className="h-3 w-28 animate-pulse rounded bg-aera-lightgray/80" />
        {Array.from({ length: 2 }, (_, index) => (
          <div
            key={index}
            className="space-y-2 rounded-xl border border-aera-lightgray bg-white px-3.5 py-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="h-3.5 w-28 animate-pulse rounded bg-aera-lightgray/80" />
                <div className="h-2.5 w-24 animate-pulse rounded bg-aera-lightgray/70" />
              </div>
              <div className="h-4 w-4 shrink-0 animate-pulse rounded-full bg-aera-lightgray/80" />
            </div>
            <div className="h-10 w-full animate-pulse rounded-lg bg-aera-lightgray/60" />
          </div>
        ))}
      </div>
      <div className="space-y-2 rounded-lg border border-aera-lightgray/80 bg-aera-offwhite/50 px-3 py-2.5">
        <div className="h-2.5 w-full max-w-xs animate-pulse rounded bg-aera-lightgray/80" />
        <div className="h-2.5 w-full max-w-sm animate-pulse rounded bg-aera-lightgray/70" />
      </div>
      <div className="flex flex-wrap gap-2">
        <div className="h-9 w-36 animate-pulse rounded-xl bg-aera-lightgray/80" />
      </div>
    </div>
  );
}

export function ResplitAllocationModal({
  open,
  onOpenChange,
  paymentId,
  paymentRemainingLabel,
  currentAllocations,
  receivableCandidates,
  ownerUserId,
  workspaceId,
  onResplit,
  embedded = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentId: string;
  paymentRemainingLabel?: string;
  currentAllocations: Array<{
    allocation_id: string;
    receivable_id: string;
    amount: string;
    label?: string;
    /** Open receivable remaining for "ค้าง …" (from money-link when not in candidates). */
    remainingLabel?: string;
  }>;
  receivableCandidates: ReceivableListItem[];
  ownerUserId: string | null;
  workspaceId: string | null;
  onResplit?: (info: ResplitDoneInfo) => void | Promise<void>;
  /** Render form inline (drawer tab) instead of a modal overlay. */
  embedded?: boolean;
}) {
  type Row = {
    receivableId: string;
    amount: string;
    label: string;
    revision: number;
    remainingLabel: string;
  };
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [committedFingerprint, setCommittedFingerprint] = useState<string | null>(
    null,
  );
  const [addListSort, setAddListSort] = useState<AddListSort>("expiry");
  const [addSortMenuOpen, setAddSortMenuOpen] = useState(false);
  const isDesktop = useMinWidthMd();
  const [scopedHost, setScopedHost] = useState<Element | null>(null);
  const allocationRailRef = useRef<HTMLDivElement | null>(null);
  const scrollToReceivableIdRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (embedded) return;
    if (!isDesktop) {
      setScopedHost(null);
      return;
    }
    if (!open) return;
    setScopedHost(document.querySelector(RECORD_DRAWER_PANEL_SELECTOR));
  }, [embedded, isDesktop, open]);

  const isScoped = !embedded && isDesktop && scopedHost != null;

  // Seed only when the modal opens — parent often passes a fresh
  // `currentAllocations` array each render, which must not wipe edits/adds.
  useEffect(() => {
    if (!open) return;

    const findCandidate = (receivableId: string) =>
      receivableCandidates.find(
        (c) =>
          (c.receivableId?.trim() || pickField(c, ["receivable_id"])) ===
          receivableId,
      );

    // One row per receivable — merge duplicate active allocations (legacy move bug).
    const byReceivable = new Map<string, Row>();
    for (const a of currentAllocations) {
      const item = findCandidate(a.receivable_id);
      const existing = byReceivable.get(a.receivable_id);
      const amountNum =
        parseAmountNumber(existing?.amount || "0") + parseAmountNumber(a.amount);
      byReceivable.set(a.receivable_id, {
        receivableId: a.receivable_id,
        amount: amountInputValue(amountNum),
        label:
          a.label ||
          existing?.label ||
          (item ? pickTitle(item) : a.receivable_id),
        revision: item ? pickReceivableRevision(item) : existing?.revision ?? 0,
        remainingLabel: item
          ? pickAmountLabel(item)
          : a.remainingLabel ||
            existing?.remainingLabel ||
            "—",
      });
    }
    setRows([...byReceivable.values()]);
    setCommittedFingerprint(null);
    setError(null);
    setBusy(false);
    setAddListSort("expiry");
    setAddSortMenuOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snapshot on open only
  }, [open]);

  const allocatedTotal = useMemo(
    () =>
      currentAllocations.reduce(
        (sum, a) => sum + parseAmountNumber(a.amount),
        0,
      ),
    [currentAllocations],
  );

  const paymentRemaining = useMemo(
    () => parseAmountNumber(paymentRemainingLabel || "0"),
    [paymentRemainingLabel],
  );

  /**
   * Pool = already matched on this slip + still unallocated.
   * Example: slip 599, matched 399 → remaining 200 → pool 599.
   * First-time allocate (no slices) uses unallocated only.
   */
  const targetTotal =
    allocatedTotal > 0.009
      ? allocatedTotal + Math.max(0, paymentRemaining)
      : paymentRemaining;

  const draftTotal = useMemo(
    () => rows.reduce((sum, row) => sum + parseAmountNumber(row.amount), 0),
    [rows],
  );

  const remainingToAssign = targetTotal - draftTotal;
  const hasPositiveRow = rows.some((r) => parseAmountNumber(r.amount) > 0);
  const balancesExact = Math.abs(remainingToAssign) < 0.005;
  const hasExistingAllocations = currentAllocations.length > 0;
  const clearingAll =
    rows.length === 0 && allocatedTotal > 0.009 && hasExistingAllocations;

  const baselineByReceivable = useMemo(() => {
    const byReceivable = new Map<string, number>();
    for (const a of currentAllocations) {
      const rid = a.receivable_id.trim();
      if (!rid) continue;
      byReceivable.set(
        rid,
        (byReceivable.get(rid) ?? 0) + parseAmountNumber(a.amount),
      );
    }
    return byReceivable;
  }, [currentAllocations]);

  const baselineFingerprint = useMemo(
    () =>
      [...baselineByReceivable.entries()]
        .map(([rid, amt]) => `${rid}:${amountInputValue(amt)}`)
        .sort()
        .join("|"),
    [baselineByReceivable],
  );

  const draftFingerprint = useMemo(
    () =>
      rows
        .map(
          (r) =>
            `${r.receivableId}:${amountInputValue(parseAmountNumber(r.amount))}`,
        )
        .sort()
        .join("|"),
    [rows],
  );

  const isDirty =
    draftFingerprint !== (committedFingerprint ?? baselineFingerprint);
  const canSubmit =
    !busy && isDirty && (clearingAll || (rows.length > 0 && hasPositiveRow));

  const addReceivable = (item: ReceivableListItem) => {
    const rid =
      item.receivableId?.trim() || pickField(item, ["receivable_id", "receivableId"]);
    if (!rid || rows.some((r) => r.receivableId === rid)) return;
    const itemCap = parseAmountNumber(pickAmountLabel(item));
    const leftover = Math.max(remainingToAssign, 0);
    // Prefill leftover so after clearing a row the next tap fills the gap.
    const seed =
      leftover > 0
        ? itemCap > 0
          ? Math.min(leftover, itemCap)
          : leftover
        : 0;
    scrollToReceivableIdRef.current = rid;
    setRows((prev) => [
      ...prev,
      {
        receivableId: rid,
        amount: amountInputValue(seed),
        label: pickTitle(item),
        revision: pickReceivableRevision(item),
        remainingLabel: pickAmountLabel(item),
      },
    ]);
  };

  useLayoutEffect(() => {
    const rid = scrollToReceivableIdRef.current;
    if (!rid) return;
    if (!rows.some((r) => r.receivableId === rid)) return;
    const rail = allocationRailRef.current;
    if (!rail) return;
    const card = rail.querySelector(
      `[data-receivable-id="${CSS.escape(rid)}"]`,
    );
    if (card instanceof HTMLElement) {
      const cardLeft = card.offsetLeft;
      const cardRight = cardLeft + card.offsetWidth;
      const viewLeft = rail.scrollLeft;
      const viewRight = viewLeft + rail.clientWidth;
      if (cardLeft < viewLeft || cardRight > viewRight) {
        const centered = cardLeft - (rail.clientWidth - card.offsetWidth) / 2;
        rail.scrollTo({
          left: Math.max(0, centered),
          behavior: "smooth",
        });
      }
    }
    scrollToReceivableIdRef.current = null;
  }, [rows]);

  const updateAmount = (receivableId: string, amount: string) => {
    const next = sanitizeAmountInput(amount);
    setRows((prev) =>
      prev.map((r) =>
        r.receivableId === receivableId ? { ...r, amount: next } : r,
      ),
    );
  };

  /**
   * Cap by payment leftover + this row's current amount, and by receivable remaining.
   * When remaining is "—" / 0 (already cleared), do not take more than this payment's
   * original slice — otherwise MAX stays enabled from leftover on other rows.
   */
  const computeMaxAmount = (
    row: Row,
    leftoverFromPool: number,
  ): number => {
    const current = parseAmountNumber(row.amount);
    const poolMax = current + Math.max(0, leftoverFromPool);
    const remainingLabel = row.remainingLabel.trim();
    const receivableCap = parseAmountNumber(remainingLabel);
    const baseline = baselineByReceivable.get(row.receivableId) ?? 0;
    const noOpenRemaining =
      !remainingLabel ||
      remainingLabel === "—" ||
      receivableCap <= 0.009;

    if (noOpenRemaining) {
      const clearedCap = Math.max(baseline, current);
      return Math.min(poolMax, clearedCap);
    }
    return Math.min(poolMax, receivableCap);
  };

  const maxAmountForRow = (row: Row): number =>
    computeMaxAmount(row, remainingToAssign);

  const fillMaxAmount = (receivableId: string) => {
    setRows((prev) => {
      const draftSum = prev.reduce(
        (sum, r) => sum + parseAmountNumber(r.amount),
        0,
      );
      const leftover = targetTotal - draftSum;
      return prev.map((r) => {
        if (r.receivableId !== receivableId) return r;
        return {
          ...r,
          amount: amountInputValue(computeMaxAmount(r, leftover)),
        };
      });
    });
  };

  const removeRow = (receivableId: string) => {
    setRows((prev) => prev.filter((r) => r.receivableId !== receivableId));
  };

  const handleSubmit = async () => {
    if (!ownerUserId?.trim() || !workspaceId?.trim() || !paymentId) return;

    const allocations = rows
      .filter((r) => parseAmountNumber(r.amount) > 0)
      .map((r) => ({
        receivable_id: r.receivableId,
        amount: r.amount,
        expected_revision: r.revision,
      }));

    // Empty save = reverse every current allocation (ยกเลิกจับคู่ → รายรับเป็นยกเลิก).
    if (allocations.length === 0) {
      if (currentAllocations.length === 0) {
        setError("ไม่มีรายการให้ยกเลิก");
        return;
      }
      setBusy(true);
      setError(null);
      try {
        // One unmatch per receivable (slices for the same pair reverse together).
        const byReceivable = new Map<string, (typeof currentAllocations)[number]>();
        for (const a of currentAllocations) {
          if (!byReceivable.has(a.receivable_id)) {
            byReceivable.set(a.receivable_id, a);
          }
        }
        let lastOpId: string | null = null;
        for (const a of byReceivable.values()) {
          const result = await postUnmatchPayment({
            ownerUserId,
            workspaceId,
            receivableId: a.receivable_id,
            paymentId,
            allocationId: a.allocation_id,
            expectedRevision: 0,
            operationKey: createOperationKey("unmatch-resplit-clear"),
          });
          if (typeof result.after?.operation_id === "string") {
            lastOpId = result.after.operation_id;
          }
        }
        setCommittedFingerprint("");
        await onResplit?.({
          operationId: lastOpId,
          label: "ยกเลิกจัดสรรทั้งหมด",
          cleared: true,
        });
        if (embedded) {
          // Inline drawer tab — stay so the user can keep editing.
        } else {
          onOpenChange(false);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "ยกเลิกจัดสรรไม่สำเร็จ",
        );
      } finally {
        setBusy(false);
      }
      return;
    }

    setBusy(true);
    setError(null);
    try {
      // No active slices yet → allocate (resplit requires existing allocations).
      const result = hasExistingAllocations
        ? await postResplitAllocation({
            ownerUserId,
            workspaceId,
            paymentId,
            allocations,
          })
        : await postAllocateMoney({
            ownerUserId,
            workspaceId,
            paymentId,
            allocations,
          });
      const opId =
        typeof result.after?.operation_id === "string"
          ? result.after.operation_id
          : null;
      await onResplit?.({
        operationId: opId,
        label: buildResplitUndoLabel(
          rows
            .filter((r) => parseAmountNumber(r.amount) > 0)
            .map((r) => ({
              label: r.label,
              amount: parseAmountNumber(r.amount),
            })),
          hasExistingAllocations ? "resplit" : "allocate",
        ),
        cleared: false,
      });
      if (embedded) {
        // Keep drawer form open so user can continue adjusting.
        setCommittedFingerprint(draftFingerprint);
      } else {
        onOpenChange(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "จัดสรรใหม่ไม่สำเร็จ");
    } finally {
      setBusy(false);
    }
  };

  const availableToAdd = useMemo(() => {
    const filtered = receivableCandidates.filter((item) => {
      if (!isReceivableUsableMoneyCandidate(item)) return false;
      if (!isReceivableAnnouncementItem(item)) return false;
      const rid =
        item.receivableId?.trim() ||
        pickField(item, ["receivable_id", "receivableId"]);
      if (!rid || rows.some((r) => r.receivableId === rid)) return false;
      // Still open to collect — hide fully cleared receivables.
      return parseAmountNumber(pickAmountLabel(item)) > 0.009;
    });
    return sortAddCandidates(filtered, addListSort);
  }, [addListSort, receivableCandidates, rows]);

  const balanceTone = balancesExact
    ? {
        wrap: "border-aera-green/30 bg-aera-greenlight/40 text-aera-charcoal",
        label:
          paymentRemaining > 0.009 || allocatedTotal > 0.009
            ? "ใช้ยอดสลิปพอดี"
            : "ตรงยอดเดิมแล้ว",
      }
    : remainingToAssign > 0
      ? {
          wrap: "border-aera-amber/30 bg-aera-warmcream/80 text-aera-amberdark",
          label: `ยังเหลือจัดสรร ฿${formatBaht(remainingToAssign)}`,
        }
      : {
          wrap: "border-aera-red/25 bg-aera-redlight/50 text-aera-red",
          label: `เกินยอดที่จัดสรรได้ ฿${formatBaht(Math.abs(remainingToAssign))}`,
        };

  const allocationParts = rows
    .filter((r) => parseAmountNumber(r.amount) > 0)
    .map(
      (r) =>
        `${r.label} ฿${formatBaht(parseAmountNumber(r.amount))}`,
    );

  const summaryLine = clearingAll ? (
    <>
      {"จัดสรรใหม่ "}
      <span className="tabular-nums">฿{formatBaht(targetTotal)}</span>
      {" — จะยกเลิกจัดสรรทั้งหมด (รายรับที่ผูกสลิปนี้จะเป็นสถานะยกเลิก)"}
    </>
  ) : allocationParts.length > 0 ? (
    <>
      {"จัดสรรใหม่ "}
      <span className="tabular-nums">฿{formatBaht(targetTotal)}</span>
      {" เป็น "}
      {allocationParts.map((part, index) => (
        <span key={`${part}-${index}`}>
          {index > 0 ? " · " : null}
          <span className="font-semibold text-aera-charcoal">{part}</span>
        </span>
      ))}
      {balanceTone.label ? <> ({balanceTone.label})</> : null}
    </>
  ) : (
    <>
      {"จัดสรรใหม่ "}
      <span className="tabular-nums">฿{formatBaht(targetTotal)}</span>
      {" — เลือกยอดค้างด้านล่าง"}
    </>
  );

  const formBody = (
      <div className="space-y-4 text-aera-charcoal">
        <div className="space-y-2">
          {embedded ? (
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-aera-lightslate">
                เลือกรายการ
              </p>
              <p className="text-xs tabular-nums text-aera-gray">
                {rows.length} รายการ
              </p>
            </div>
          ) : null}

          {rows.length === 0 ? (
            <div className="space-y-2 rounded-xl border border-dashed border-aera-amber/40 bg-aera-offwhite px-4 py-7.5 text-center">
              <p className="text-sm font-semibold text-aera-charcoal">
                {clearingAll
                  ? "ลบรายการจัดสรรครบแล้ว — ยังไม่ได้บันทึก"
                  : hasExistingAllocations && allocatedTotal > 0.009
                    ? "ลบรายการร่างแล้ว — ยังไม่ได้บันทึก"
                    : "ยังไม่มีรายการจัดสรร"}
              </p>
              <p className="text-xs leading-relaxed text-aera-lightslate">
                {clearingAll
                  ? "กดบันทึกเพื่อยกเลิกจับคู่ และยกเลิกรายรับที่ผูกกับสลิปนี้ หรือเลือกยอดค้างด้านล่างเพื่อจัดสรรใหม่"
                  : hasExistingAllocations && allocatedTotal > 0.009
                    ? `เลือกยอดค้างด้านล่าง ใส่ยอดให้ครบ ฿${formatBaht(targetTotal)} แล้วกดบันทึก`
                    : targetTotal > 0.009
                      ? `กดเลือกยอดค้างด้านล่าง — สลิปรอจัดสรร ฿${formatBaht(targetTotal)}`
                      : "กดเลือกยอดค้างด้านล่างเพื่อเริ่มจัดสรร"}
              </p>
              {availableToAdd.length === 0 && !clearingAll ? (
                <p className="text-xs text-aera-amberdark">
                  ไม่มียอดค้างให้เลือก — ใช้ปุ่มสร้างยอดค้างใหม่ด้านล่างก่อน
                </p>
              ) : null}
            </div>
          ) : (
            <TooltipProvider delayDuration={200}>
              <CardScrollRail
                syncKey={rows.map((r) => r.receivableId).join("|")}
                railClassName={ALLOCATION_RAIL_CLASSNAME}
                hideButtonsOnMobile={false}
                defaultItemWidth={220}
                railRef={allocationRailRef}
              >
                {rows.map((row) => {
                  const maxAmt = maxAmountForRow(row);
                  const currentAmt = parseAmountNumber(row.amount);
                  const noRoom = maxAmt <= 0.009;
                  const alreadyMax = Math.abs(currentAmt - maxAmt) < 0.005;
                  const maxDisabled = busy || noRoom || alreadyMax;
                  const receivableCap = parseAmountNumber(row.remainingLabel);
                  const maxDisabledReason = busy
                    ? "กำลังทำงานอยู่ กรุณารอสักครู่"
                    : alreadyMax
                      ? receivableCap > 0.009 &&
                        Math.abs(maxAmt - receivableCap) < 0.005
                        ? `ยอดเต็มตามยอดค้างแล้ว (฿${formatBaht(maxAmt)})`
                        : `ยอดเต็มตามยอดที่จัดสรรได้แล้ว (฿${formatBaht(maxAmt)})`
                      : noRoom
                        ? "ไม่มียอดเหลือให้จัดสรรเพิ่ม"
                        : undefined;

                  const maxButton = (
                    <button
                      type="button"
                      disabled={maxDisabled}
                      onClick={() => fillMaxAmount(row.receivableId)}
                      className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide opacity-100 transition-colors ${
                        maxDisabled
                          ? "cursor-not-allowed text-aera-gray pointer-events-none"
                          : "cursor-pointer text-aera-amberdark hover:bg-aera-amber/15"
                      }`}
                      aria-label={
                        maxDisabledReason
                          ? `MAX — ${maxDisabledReason}`
                          : `ใส่ยอดสูงสุดของ ${row.label}`
                      }
                    >
                      MAX
                    </button>
                  );

                  return (
                    <article
                      key={row.receivableId}
                      data-receivable-id={row.receivableId}
                      className="w-[220px] shrink-0 rounded-xl border border-aera-lightgray bg-white px-3.5 py-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-aera-charcoal">
                            {row.label}
                          </p>
                          <p className="mt-0.5 text-xs tabular-nums text-aera-gray">
                            ค้าง {row.remainingLabel}
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => removeRow(row.receivableId)}
                          className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-aera-red transition-colors hover:bg-aera-redlight disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label={`ลบ ${row.label}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </button>
                      </div>
                      <div className="mt-2 flex items-center gap-1.5 rounded-lg border border-aera-lightgray bg-aera-offwhite/60 px-2.5 py-2 focus-within:border-aera-amber">
                        <span className="text-sm font-semibold text-aera-gray">
                          ฿
                        </span>
                        <input
                          className="min-w-0 flex-1 bg-transparent text-sm font-semibold tabular-nums text-aera-charcoal outline-none"
                          value={row.amount}
                          disabled={busy}
                          inputMode="decimal"
                          placeholder="0"
                          aria-label={`ยอดจัดสรร ${row.label}`}
                          onChange={(e) =>
                            updateAmount(row.receivableId, e.target.value)
                          }
                        />
                        {maxDisabledReason ? (
                          <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild>
                              <span className="inline-flex shrink-0 cursor-not-allowed">
                                {maxButton}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              sideOffset={6}
                              collisionPadding={12}
                              className="z-[10100] w-max max-w-[14rem] whitespace-normal wrap-break-word bg-aera-white text-left leading-relaxed"
                            >
                              <p>{maxDisabledReason}</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="inline-flex shrink-0">{maxButton}</span>
                        )}
                      </div>
                    </article>
                  );
                })}
              </CardScrollRail>
            </TooltipProvider>
          )}
        </div>

        {availableToAdd.length > 0 ? (
          <div
            className={`space-y-2 rounded-xl border px-3 py-3 ${
              rows.length === 0
                ? "border-aera-amber/50 bg-aera-warmcream ring-2 ring-aera-amber/20"
                : "border-dashed border-aera-lightgray bg-aera-offwhite"
            }`}
          >
            <div className="flex flex-nowrap items-center justify-between gap-2">
              <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
                <Plus className="h-3.5 w-3.5 shrink-0 text-aera-amber" aria-hidden />
                <p className="truncate text-xs font-semibold text-aera-charcoal">
                  {rows.length === 0
                    ? "กดเลือกยอดค้างด้านล่างนี้"
                    : "จับคู่ยอดค้างเพิ่ม"}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <span className="hidden text-[11px] font-medium text-aera-gray sm:inline">
                  เรียงตาม
                </span>
                <DropdownMenu
                  open={addSortMenuOpen}
                  onOpenChange={setAddSortMenuOpen}
                >
                  <DropdownMenuTrigger asChild>
                    <AeraButton
                      type="button"
                      variant="outline"
                      size="xs"
                      fontWeight="semibold"
                      className="min-w-0 max-w-[9.5rem]"
                      disabled={busy}
                      aria-label={`เรียงตาม: ${
                        ADD_LIST_SORT_OPTIONS.find((o) => o.id === addListSort)
                          ?.label ?? ""
                      }`}
                      aria-expanded={addSortMenuOpen}
                      iconRight={
                        <ChevronDown
                          size={14}
                          className={`shrink-0 text-aera-gray transition-transform duration-200 ${
                            addSortMenuOpen ? "rotate-180" : "rotate-0"
                          }`}
                        />
                      }
                    >
                      <span className="truncate">
                        {ADD_LIST_SORT_OPTIONS.find((o) => o.id === addListSort)
                          ?.label ?? "ใกล้หมดอายุ"}
                      </span>
                    </AeraButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    sideOffset={6}
                    className="z-[10100] min-w-[11.5rem] rounded-xl border border-aera-lightgray/80 p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)]"
                  >
                    {ADD_LIST_SORT_OPTIONS.map((option) => {
                      const active = addListSort === option.id;
                      return (
                        <DropdownMenuItem
                          key={option.id}
                          onClick={() => {
                            setAddListSort(option.id);
                            setAddSortMenuOpen(false);
                          }}
                          className="cursor-pointer justify-between gap-3 rounded-lg px-2.5 py-2 text-xs font-semibold text-aera-charcoal"
                        >
                          <span>{option.label}</span>
                          {active ? (
                            <Check
                              className="h-3.5 w-3.5 shrink-0 text-aera-amber"
                              strokeWidth={3}
                              aria-hidden
                            />
                          ) : (
                            <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            <CardScrollRail
              syncKey={`${addListSort}:${availableToAdd.map((i) => i.mergeKey).join("|")}`}
              railClassName={ALLOCATION_RAIL_CLASSNAME}
              hideButtonsOnMobile={false}
              defaultItemWidth={168}
              edgeFadeFromClassName={
                rows.length === 0
                  ? "from-aera-warmcream via-aera-warmcream/80"
                  : "from-aera-offwhite via-aera-offwhite/80"
              }
            >
              {availableToAdd.map((item) => (
                <button
                  key={item.mergeKey}
                  type="button"
                  disabled={busy}
                  onClick={() => addReceivable(item)}
                  className="inline-flex w-[168px] shrink-0 cursor-pointer flex-col items-start gap-0.5 rounded-lg border border-aera-lightgray bg-white px-2.5 py-2 text-left transition-colors hover:border-aera-amber/50 hover:bg-aera-amber/5 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="w-full truncate text-xs font-semibold text-aera-charcoal">
                    {pickTitle(item)}
                  </span>
                  <span className="text-[11px] tabular-nums text-aera-gray">
                    {pickAmountLabel(item)}
                  </span>
                </button>
              ))}
            </CardScrollRail>
          </div>
        ) : null}

        {embedded && (targetTotal > 0 || draftTotal > 0 || clearingAll) ? (
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-medium text-aera-gray">สลิปนี้</p>
              <p className="text-sm font-semibold tabular-nums text-aera-charcoal">
                ฿{formatBaht(targetTotal)}
              </p>
            </div>
            {clearingAll ? (
              <div className="shrink-0 text-right">
                <p className="text-xs font-medium text-aera-gray">ผลลัพธ์</p>
                <p className="text-sm font-semibold text-aera-red">
                  ยกเลิกทั้งหมด
                </p>
              </div>
            ) : rows.length > 0 || draftTotal > 0.009 ? (
              <div className="shrink-0 text-right">
                <p className="text-xs font-medium text-aera-gray">
                  เลือกจับคู่{" "}
                  <span className="font-semibold tabular-nums text-aera-amberdark">
                    ฿{formatBaht(draftTotal)}
                  </span>
                </p>
                <p
                  className={`text-sm font-semibold tabular-nums ${
                    remainingToAssign < -0.005
                      ? "text-aera-red"
                      : remainingToAssign > 0.005
                        ? "text-aera-green"
                        : "text-aera-charcoal"
                  }`}
                >
                  {remainingToAssign < -0.005
                    ? `เกิน ฿${formatBaht(Math.abs(remainingToAssign))}`
                    : remainingToAssign > 0.005
                      ? `จะเหลือ ฿${formatBaht(remainingToAssign)}`
                      : "ใช้ยอดสลิปพอดี"}
                </p>
              </div>
            ) : (
              <p className="max-w-[55%] shrink-0 text-right text-xs leading-snug text-aera-gray">
                เลือกยอดค้างเพื่อดูยอดที่จะเหลือ
              </p>
            )}
          </div>
        ) : null}

        {error ? (
          <p className="rounded-lg bg-aera-redlight px-3 py-2 text-sm font-medium text-aera-red">
            {error}
          </p>
        ) : null}
      </div>
  );

  const close = () => {
    if (busy) return;
    onOpenChange(false);
  };

  if (embedded) {
    if (!open) return null;
    return (
      <div className="space-y-4 rounded-2xl border border-(--aera-dt-border-soft) bg-(--aera-dt-surface-elevated) p-4">
        {formBody}
        {isDirty ? (
          <div className="flex flex-wrap justify-end gap-2">
              <AeraButton
                type="button"
                variant={clearingAll ? "danger" : "primary"}
                size="sm"
                fontWeight="medium"
                disabled={!canSubmit}
                onClick={() => void handleSubmit()}
              >
                {busy
                  ? "กำลังบันทึก…"
                  : clearingAll
                    ? "บันทึก — ยกเลิกจัดสรร"
                    : "บันทึก"}
              </AeraButton>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <ModalShell
      isOpen={open}
      onClose={close}
      onExited={() => setScopedHost(null)}
      align="bottom"
      scopedHost={isScoped ? scopedHost : null}
      preventBackdropClose={busy}
      zIndexClass="z-[10050]"
      panelClassName={[
        isScoped
          ? "flex flex-col overflow-hidden rounded-t-3xl rounded-b-[inherit] border-t border-aera-lightgray bg-aera-white shadow-xl"
          : "flex flex-col overflow-hidden rounded-t-3xl rounded-b-none border-t border-aera-lightgray bg-aera-white shadow-xl",
        isScoped ? "max-h-[85%]" : "max-h-[80dvh]",
      ].join(" ")}
    >
      <div className="flex shrink-0 justify-center pb-1 pt-2">
        <div className="h-1 w-10 rounded-full bg-aera-lightgray" aria-hidden />
      </div>

      <header className="flex shrink-0 items-start justify-between gap-3 px-4 pb-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-aera-charcoal">
            <span className="flex items-center gap-2 text-aera-amberdark">
              <Split className="h-5 w-5 shrink-0" strokeWidth={2} aria-hidden />
              ยืนยันจัดสรรใหม่
            </span>
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-aera-gray">
            {summaryLine}
          </p>
        </div>
        <ModalCloseButton
          onClose={close}
          disabled={busy}
          className="-mr-1 shrink-0"
        />
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {formBody}
      </div>

      <div className="shrink-0 space-y-3 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {isDirty ? (
          <AeraButton
            type="button"
            variant={clearingAll ? "danger" : "primary"}
            fontWeight="medium"
            fullWidth
            className="cursor-pointer rounded-full!"
            disabled={!canSubmit}
            loading={busy}
            onClick={() => void handleSubmit()}
          >
            {busy
              ? "กำลังบันทึก…"
              : clearingAll
                ? "ยืนยันยกเลิกจัดสรร"
                : "ยืนยันจัดสรร"}
          </AeraButton>
        ) : null}
        <AeraButton
          type="button"
          variant="outline"
          fontWeight="medium"
          fullWidth
          className="cursor-pointer rounded-full!"
          disabled={busy}
          onClick={close}
        >
          ปิด
        </AeraButton>
      </div>
    </ModalShell>
  );
}
